import { FC, memo, useEffect } from 'react';
import { useGetMyLeadsQuery } from '@api/apiCompat';
import { ERROR } from '@constants/auth';
import { API_ERROR_MSG_PATH } from '@constants/common';
import { useCustomFiltersQueryParam } from '@hooks/common/useDynamicFilters/useCustomFiltersQueryParam';
import { usePageFilterUrlParams } from '@hooks/common/usePageFilterUrlParams';
import { transformQuery } from '@pages/CampaignDetails/utils';
import { getErrorMessage } from '@utils/getMessage';
import { useSnackbar } from 'notistack';
import qs from 'qs';

import { MyLeadsTable } from '@components/LeadsTable';
import { ExtendedPaginatedLeadsData } from '@components/LeadsTable/types';

// Map lead_type filter value to backend campaignType ID
const LEAD_TYPE_TO_CAMPAIGN_TYPE: Record<string, number> = {
  local_search: 1,
  real_estate: 3,
  jobs: 4,
  social_media: 5,
};

const LEAD_TYPE_TO_COLUMN_TYPE: Record<string, string> = {
  local_search: 'Local Search',
  real_estate: 'Real Estate',
  jobs: 'Job Sites',
  social_media: 'Social Media',
};

const MyLeads: FC = () => {
  const { page, limit } = usePageFilterUrlParams();
  const { dynamicParam } = useCustomFiltersQueryParam();
  const snackbar = useSnackbar();

  // Extract lead_type from filters
  // AutocompleteSearchFilter stores [{label, value}] arrays
  let leadTypeFilter = '';
  if (dynamicParam) {
    try {
      const parsed = JSON.parse(dynamicParam);
      const lt = parsed?.lead_type;
      if (Array.isArray(lt) && lt.length > 0) {
        leadTypeFilter = lt[0]?.value || '';
      } else if (typeof lt === 'string') {
        leadTypeFilter = lt;
      }
    } catch {
      // ignore
    }
  }

  const campaignTypeId = leadTypeFilter ? LEAD_TYPE_TO_CAMPAIGN_TYPE[leadTypeFilter] : undefined;
  const columnType = leadTypeFilter ? LEAD_TYPE_TO_COLUMN_TYPE[leadTypeFilter] : 'Local Search';

  const serializedFilters =
    dynamicParam && encodeURIComponent(qs.stringify(transformQuery(dynamicParam), { encode: false }));

  const {
    data: myLeadsData,
    isLoading: isLoadingMyLeads,
    isFetching: isFetchingMyLeads,
    refetch: refetchMyLeads,
    error: myLeadsError,
  } = (useGetMyLeadsQuery as any)(
    {
      page,
      limit,
      ...(campaignTypeId && { campaignType: campaignTypeId }),
      ...(dynamicParam && { filters: serializedFilters }),
    },
    { refetchOnMountOrArgChange: true },
  );

  const isLoading = [isLoadingMyLeads, isFetchingMyLeads].some(Boolean);

  useEffect(() => {
    if (myLeadsError) {
      snackbar.enqueueSnackbar(getErrorMessage([myLeadsError], API_ERROR_MSG_PATH), { variant: ERROR });
    }
  }, [myLeadsError, snackbar]);

  return (
    <MyLeadsTable
      totalLeadsFound={(myLeadsData as any)?.total}
      leadsData={myLeadsData as ExtendedPaginatedLeadsData}
      isLoadingLeads={isLoading}
      refetch={refetchMyLeads}
      campaignTypeId={campaignTypeId ? String(campaignTypeId) : undefined}
      campaignType={columnType}
      serializedFilters={serializedFilters}
    />
  );
};

export default memo(MyLeads);
